import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { Loader2, DollarSign, ShoppingCart, Users, TrendingUp, Calendar } from "lucide-react";
import clsx from "clsx";
import i18n from "@/i18n";
import { formatMoneyCompact } from "@/lib/format";

// Components
import {
  StatCard,
  RevenueChart,
  DestinationPieChart,
  OrdersChart,
  FunnelChart,
  OccupancyTable,
  GuideStats,
  CustomerStats,
  MarketingStats,
  TrafficChart,
  AdsPerformance,
} from "@/components/analytics";

// Types
interface AnalyticsData {
  summary: {
    revenue: {
      current: number;
      previousPeriod: number;
      previousYear: number;
      changeMoM: number;
      changeYoY: number;
    };
    orders: {
      current: number;
      previous: number;
      change: number;
    };
    occupancy: {
      current: number;
      previous: number;
      change: number;
    };
    customers: {
      total: number;
      new: number;
      returning: number;
      retentionRate: number;
    };
  };
  revenue: {
    byMonth: Array<{ month: string; current: number; previousYear: number }>;
    byDestination: Array<{
      destinationId: string;
      destination: string;
      revenue: number;
      percentage: number;
    }>;
    forecast: {
      confirmed: number;
      depositPaid: number;
      estimatedTotal: number;
    };
    aov: number;
    revenuePerParticipant: number;
  };
  orders: {
    byStatus: {
      inquiry: number;
      quoted: number;
      confirmed: number;
      deposit_paid: number;
      fully_paid: number;
      completed: number;
      cancelled: number;
    };
    funnel: {
      inquiryToQuoted: number;
      quotedToConfirmed: number;
      confirmedToPaid: number;
      overallConversion: number;
    };
    cancellationRate: number;
    avgLeadTime: number;
    avgBookingWindow: number;
    byMonth: Array<{ month: string; count: number; revenue: number }>;
  };
  trips: {
    topTrips: Array<{
      id: string;
      title: string;
      revenue: number;
      bookings: number;
      occupancy: number;
    }>;
    lowOccupancy: Array<{
      id: string;
      title: string;
      dateId: string;
      startDate: string;
      occupancy: number;
    }>;
    trendingDestinations: Array<{
      destination: string;
      currentCount: number;
      previousCount: number;
      changeYoY: number;
    }>;
    occupancyByMonth: Array<{ month: string; avgOccupancy: number }>;
    avgGroupSize: number;
  };
  customers: {
    byCountry: Array<{ country: string; count: number; percentage: number }>;
    topCustomers: Array<{
      id: string;
      name: string;
      totalSpent: number;
      tripsCount: number;
    }>;
    avgLifetimeValue: number;
    newVsReturning: { new: number; returning: number };
    growthByMonth: Array<{ month: string; newCustomers: number }>;
  };
  guides: {
    utilization: Array<{
      id: string;
      name: string;
      tripsCount: number;
      avgRating: number;
      revenue: number;
    }>;
    unassigned: Array<{
      tripId: string;
      tripTitle: string;
      dateId: string;
      startDate: string;
    }>;
    calendarData: Array<{
      guideId: string;
      guideName: string;
      dates: string[];
    }>;
  };
  marketing: {
    bySource: Array<{ source: string; count: number; revenue: number }>;
    topCampaigns: Array<{
      utm_campaign: string;
      count: number;
      revenue: number;
      conversion: number;
    }>;
    newsletterStats: {
      subscribers: number;
      growthLastMonth: number;
    };
  };
}

type Tab =
  | "revenue"
  | "orders"
  | "trips"
  | "customers"
  | "guides"
  | "marketing"
  | "traffic"
  | "ads";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "revenue", label: i18n.t("system.analytics.tabs.revenue") },
  { key: "orders", label: i18n.t("system.analytics.tabs.orders") },
  { key: "trips", label: i18n.t("system.analytics.tabs.trips") },
  { key: "customers", label: i18n.t("system.analytics.tabs.customers") },
  { key: "guides", label: i18n.t("system.analytics.tabs.guides") },
  { key: "marketing", label: i18n.t("system.analytics.tabs.marketing") },
  { key: "traffic", label: i18n.t("system.analytics.tabs.traffic") },
  { key: "ads", label: i18n.t("system.analytics.tabs.ads") },
];

// Traffic data types
interface TrafficData {
  destinations: Array<{
    path: string;
    destinationId: string | null;
    name: string;
    pageViews: number;
    sessions: number;
    avgSessionDuration: number;
    bounceRate: number;
  }>;
  trips: Array<{
    path: string;
    tripId: string | null;
    title: string;
    pageViews: number;
    sessions: number;
    bounceRate: number;
  }>;
  overview: {
    totalPageViews: number;
    totalSessions: number;
    avgBounceRate: number;
    avgSessionDuration: number;
  };
  byMonth: Array<{
    month: string;
    pageViews: number;
    sessions: number;
  }>;
}

// Ads data types
interface AdsData {
  campaigns: Array<{
    id: string;
    name: string;
    cost: number;
    impressions: number;
    clicks: number;
    ctr: number;
    conversions: number;
    conversionValue: number;
    cpa: number;
    roas: number;
  }>;
  byDestination: Array<{
    destination: string;
    destinationId: string | null;
    destinationSlug: string;
    totalCost: number;
    totalConversions: number;
    totalRevenue: number;
    roas: number;
    cpa: number;
    profitable: boolean;
  }>;
  summary: {
    totalSpend: number;
    totalConversions: number;
    totalRevenue: number;
    overallRoas: number;
    avgCpa: number;
  };
  byMonth: Array<{
    month: string;
    spend: number;
    conversions: number;
    revenue: number;
  }>;
  recommendations: Array<{
    destination: string;
    destinationId: string | null;
    issue: string;
    suggestion: string;
    priority: "high" | "medium" | "low";
  }>;
}

const PERIODS = [
  { value: "month", label: i18n.t("system.analytics.periods.month") },
  { value: "quarter", label: i18n.t("system.analytics.periods.quarter") },
  { value: "year", label: i18n.t("system.analytics.periods.year") },
];

const DEFAULT_TRAFFIC_OVERVIEW = {
  totalPageViews: 0,
  totalSessions: 0,
  avgBounceRate: 0,
  avgSessionDuration: 0,
};

const DEFAULT_ADS_SUMMARY = {
  totalSpend: 0,
  totalConversions: 0,
  totalRevenue: 0,
  overallRoas: 0,
  avgCpa: 0,
};

function RevenueTabContent({ revenue }: { revenue: AnalyticsData["revenue"] }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RevenueChart data={revenue.byMonth} />
        </div>
        <DestinationPieChart data={revenue.byDestination} />
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t("system.analytics.revenue.aov")}
          </p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatMoneyCompact(revenue.aov)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t("system.analytics.revenue.perParticipant")}
          </p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatMoneyCompact(revenue.revenuePerParticipant)}
          </p>
        </div>
        <div className="card lg:col-span-2">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t("system.analytics.revenue.forecast")}
          </p>
          <p className="text-primary-600 dark:text-primary-400 mt-2 text-3xl font-bold">
            {formatMoneyCompact(revenue.forecast.estimatedTotal)}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {t("system.analytics.revenue.forecastDetail", {
              confirmed: formatMoneyCompact(revenue.forecast.confirmed),
              deposit: formatMoneyCompact(revenue.forecast.depositPaid),
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

function OrdersTabContent({ orders }: { orders: AnalyticsData["orders"] }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <OrdersChart data={orders.byStatus} />
      <FunnelChart funnel={orders.funnel} cancellationRate={orders.cancellationRate} />
    </div>
  );
}

function TrafficTabContent({
  traffic,
  isLoading: loading,
  error: err,
}: {
  traffic: TrafficData | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  return (
    <TrafficChart
      destinations={traffic?.destinations || []}
      trips={traffic?.trips || []}
      overview={traffic?.overview || DEFAULT_TRAFFIC_OVERVIEW}
      byMonth={traffic?.byMonth || []}
      isLoading={loading}
      error={err?.message || null}
    />
  );
}

function AdsTabContent({
  ads,
  isLoading: loading,
  error: err,
}: {
  ads: AdsData | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  return (
    <AdsPerformance
      campaigns={ads?.campaigns || []}
      byDestination={ads?.byDestination || []}
      summary={ads?.summary || DEFAULT_ADS_SUMMARY}
      byMonth={ads?.byMonth || []}
      recommendations={ads?.recommendations || []}
      isLoading={loading}
      error={err?.message || null}
    />
  );
}

function AnalyticsTabContent({
  activeTab,
  analytics,
  traffic,
  trafficLoading,
  trafficError,
  ads,
  adsLoading,
  adsError,
}: {
  activeTab: Tab;
  analytics: AnalyticsData;
  traffic: TrafficData | undefined;
  trafficLoading: boolean;
  trafficError: Error | null;
  ads: AdsData | undefined;
  adsLoading: boolean;
  adsError: Error | null;
}) {
  switch (activeTab) {
    case "revenue":
      return <RevenueTabContent revenue={analytics.revenue} />;
    case "orders":
      return <OrdersTabContent orders={analytics.orders} />;
    case "trips":
      return (
        <OccupancyTable
          topTrips={analytics.trips.topTrips}
          lowOccupancy={analytics.trips.lowOccupancy}
          trendingDestinations={analytics.trips.trendingDestinations}
          avgGroupSize={analytics.trips.avgGroupSize}
        />
      );
    case "customers":
      return (
        <CustomerStats
          byCountry={analytics.customers.byCountry}
          topCustomers={analytics.customers.topCustomers}
          avgLifetimeValue={analytics.customers.avgLifetimeValue}
          newVsReturning={analytics.customers.newVsReturning}
          growthByMonth={analytics.customers.growthByMonth}
        />
      );
    case "guides":
      return (
        <GuideStats
          utilization={analytics.guides.utilization}
          unassigned={analytics.guides.unassigned}
          calendarData={analytics.guides.calendarData}
        />
      );
    case "marketing":
      return (
        <MarketingStats
          bySource={analytics.marketing.bySource}
          topCampaigns={analytics.marketing.topCampaigns}
          newsletterStats={analytics.marketing.newsletterStats}
        />
      );
    case "traffic":
      return (
        <TrafficTabContent traffic={traffic} isLoading={trafficLoading} error={trafficError} />
      );
    case "ads":
      return <AdsTabContent ads={ads} isLoading={adsLoading} error={adsError} />;
  }
}

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>("revenue");
  const [period, setPeriod] = useState("year");
  const [year] = useState(new Date().getFullYear());

  const { data, isLoading, error } = useQuery({
    queryKey: ["analytics", period, year],
    queryFn: () =>
      api.get<ApiResponse<AnalyticsData>>(`/api/admin/analytics?period=${period}&year=${year}`),
  });

  // Traffic data query (only fetch when tab is active)
  const {
    data: trafficData,
    isLoading: trafficLoading,
    error: trafficError,
  } = useQuery({
    queryKey: ["analytics-traffic", period, year],
    queryFn: () =>
      api.get<ApiResponse<TrafficData>>(
        `/api/admin/analytics/traffic?period=${period}&year=${year}`,
      ),
    enabled: activeTab === "traffic",
  });

  // Ads data query (only fetch when tab is active)
  const {
    data: adsData,
    isLoading: adsLoading,
    error: adsError,
  } = useQuery({
    queryKey: ["analytics-ads", period, year],
    queryFn: () =>
      api.get<ApiResponse<AdsData>>(`/api/admin/analytics/ads?period=${period}&year=${year}`),
    enabled: activeTab === "ads",
  });

  const analytics = data?.data;
  const traffic = trafficData?.data;
  const ads = adsData?.data;

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-red-500">{t("system.analytics.loadFailed")}</p>
        <p className="mt-2 text-sm text-gray-500">
          {error?.message || t("system.analytics.tryAgain")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("nav.items.analytics")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("system.analytics.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-400" />
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input w-44">
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t("system.analytics.summary.revenue")}
          value={analytics.summary.revenue.current}
          format="currency"
          change={analytics.summary.revenue.changeMoM}
          changeLabel={t("system.analytics.mom")}
          secondaryChange={analytics.summary.revenue.changeYoY}
          secondaryLabel={t("system.analytics.yoy")}
          icon={DollarSign}
          iconColor="text-green-500"
        />
        <StatCard
          title={t("system.analytics.summary.orders")}
          value={analytics.summary.orders.current}
          format="number"
          change={analytics.summary.orders.change}
          changeLabel={t("system.analytics.mom")}
          icon={ShoppingCart}
          iconColor="text-blue-500"
        />
        <StatCard
          title={t("system.analytics.summary.occupancy")}
          value={analytics.summary.occupancy.current}
          format="percent"
          change={analytics.summary.occupancy.change}
          changeLabel={t("system.analytics.mom")}
          icon={TrendingUp}
          iconColor="text-purple-500"
        />
        <StatCard
          title={t("system.analytics.summary.customers")}
          value={analytics.summary.customers.total}
          format="number"
          subtitle={t("system.analytics.summary.newCount", {
            count: analytics.summary.customers.new,
          })}
          icon={Users}
          iconColor="text-amber-500"
        />
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              "whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              activeTab === tab.key
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        <AnalyticsTabContent
          activeTab={activeTab}
          analytics={analytics}
          traffic={traffic}
          trafficLoading={trafficLoading}
          trafficError={trafficError}
          ads={ads}
          adsLoading={adsLoading}
          adsError={adsError}
        />
      </div>
    </div>
  );
}
